import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiMenu, FiUser, FiGlobe } from "react-icons/fi";
import {
  LeftSection,
  MobileMenuToggle,
  TopBarItem,
  RightSection,
  Tooltip,
  TooltipItem,
  TopBarContainer,
  Label,
} from "./styles";
import { HorizontalMenu } from "components/HorizontalMenu/HorizontalMenu";
import { useAuth } from "context/AuthContext";
import { useMutation } from "react-query";
import { useAxiosPrivate } from "hooks/api/useAxiosPrivate";
import { createIcon } from "utils/iconUtils";

interface TopBarProps {
  onSidebarToggle?: () => void;
}

const MenuIcon = createIcon<{ size?: number }>(FiMenu);
const GlobeIcon = createIcon<{ size?: number }>(FiGlobe);
const UserIcon = createIcon<{ size?: number }>(FiUser);

export const TopBar: React.FC<TopBarProps> = ({ onSidebarToggle }) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isLanguageTooltipVisible, setIsLanguageTooltipVisible] =
    useState(false);
  const languageItemRef = useRef<HTMLDivElement>(null);
  const userItemRef = useRef<HTMLDivElement>(null);

  const { i18n } = useTranslation();
  const location = useLocation();
  const isDocumentationRoute = location.pathname.startsWith("/documentation/");
  const navigate = useNavigate();

  const { clearAuth } = useAuth();

  const axiosPrivate = useAxiosPrivate();

  const logoutMutation = useMutation(
    () => {
      return axiosPrivate.post(`/logout`);
    },
    {
      onSuccess: (data) => {
        clearAuth();
      },
      onError: (error) => {
        clearAuth();
      },
    }
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (
      languageItemRef.current &&
      !languageItemRef.current.contains(event.target as Node)
    ) {
      setIsLanguageTooltipVisible(false);
    }
    if (
      userItemRef.current &&
      !userItemRef.current.contains(event.target as Node)
    ) {
      setIsTooltipVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <TopBarContainer>
      <MobileMenuToggle onClick={onSidebarToggle}>
        <MenuIcon size={20} />
      </MobileMenuToggle>
      <LeftSection>
        <HorizontalMenu />
      </LeftSection>
      <RightSection>
        {isDocumentationRoute && (
          <>
            <Label>Select Documentation Language :</Label>
            <TopBarItem
              ref={languageItemRef}
              onClick={() =>
                setIsLanguageTooltipVisible(!isLanguageTooltipVisible)
              }
            >
              <GlobeIcon size={20} />
              <Tooltip $isVisible={isLanguageTooltipVisible}>
                <TooltipItem onClick={() => i18n.changeLanguage("en")}>
                  English
                </TooltipItem>
                <TooltipItem onClick={() => i18n.changeLanguage("fr")}>
                  French
                </TooltipItem>
                <TooltipItem onClick={() => i18n.changeLanguage("ar")}>
                  Arabic
                </TooltipItem>
              </Tooltip>
            </TopBarItem>
          </>
        )}
        <TopBarItem
          ref={userItemRef}
          onClick={() => setIsTooltipVisible(!isTooltipVisible)}
        >
          <UserIcon size={20} />
          <Tooltip $isVisible={isTooltipVisible}>
            <TooltipItem onClick={() => navigate("/profile")}>
              Profile
            </TooltipItem>
            {/* TODO: Manage Logout */}
            <TooltipItem onClick={() => logoutMutation.mutate()}>
              Logout
            </TooltipItem>
          </Tooltip>
        </TopBarItem>
      </RightSection>
    </TopBarContainer>
  );
};
